import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, ExternalLink, Target, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { SiteFooter, SiteHeader } from "@/components/SiteChrome";
import AmbassadorRow from "@/components/leaderboard/AmbassadorRow";

const COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];

export default function PublicLeaderboard() {
  useSeo({
    title: "Leaderboard — NoobSync Brand Ambassadors",
    description: "See the live NoobSync Brand Ambassador leaderboard. Track the top performers by points, referrals, and content across the current cohort — updated in real time.",
    path: "/leaderboard",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard?limit=10").then((r) => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const rankedList = (data?.leaderboard || []).filter((e) => e.ranked);
  const top3 = rankedList.slice(0, 3);
  const rest = rankedList.slice(3);
  const winners = data?.winners;
  const showPoints = data?.show_points_public !== false;
  const cohortName = data?.cohort?.name || "";

  return (
    <div className="min-h-screen bg-transparent text-white">
      <SiteHeader />

      <section className="relative overflow-hidden border-b border-zinc-900">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[420px] -translate-x-1/2 radial-violet opacity-50" />
        <div className="relative mx-auto max-w-6xl px-5 py-16 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">
            {cohortName ? `${cohortName} • Live leaderboard` : "Live leaderboard"}
          </p>
          <h1 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-6xl">
            Who's leading the pack
          </h1>
          {winners ? (
            <p className="mt-3 text-zinc-400">Final results — Cohort complete 🏆</p>
          ) : (
            <p className="mt-3 text-zinc-400">Updated in real time. Top 3 win exclusive rewards.</p>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-12">
        {loading && (
          <div className="text-center text-zinc-500" data-testid="lb-loading">Loading…</div>
        )}

        {!loading && data && data.is_public_ready === false && (
          <div data-testid="lb-warming-up-full" className="rounded-2xl border border-dashed border-zinc-800 bg-zinc-950/60 p-16 text-center">
            <Trophy className="mx-auto h-12 w-12 text-zinc-700" />
            <p className="mt-4 font-display text-2xl text-zinc-200">
              The leaderboard is warming up
            </p>
            <p className="mt-2 text-sm text-zinc-500">
              Top 10 will be revealed once the cohort gets going. Apply now to be one of the first names on the board when it goes live.
            </p>
            <Link
              to="/apply"
              data-testid="lb-warming-apply"
              className="mt-6 inline-flex rounded-md brand-gradient-bg px-6 py-3 font-bold text-white hover:-translate-y-0.5 transition-transform"
            >
              Apply now
            </Link>
          </div>
        )}

        {!loading && data?.is_public_ready !== false && !data?.leaderboard?.length && (
          <div data-testid="lb-empty-full" className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/60 p-16 text-center">
            <Trophy className="mx-auto h-12 w-12 text-zinc-700" />
            <p className="mt-4 font-display text-xl text-zinc-300">
              Leaderboard goes live soon
            </p>
            <p className="mt-1 text-sm text-zinc-500">Apply now — be one of the first names on the board.</p>
            <Link
              to="/apply"
              data-testid="lb-empty-apply"
              className="mt-6 inline-flex rounded-md bg-[#A855F7] px-6 py-3 font-bold text-white hover:-translate-y-0.5 transition-transform"
            >
              Apply now
            </Link>
          </div>
        )}

        {data?.is_public_ready !== false && top3.length > 0 && (
          <div className="grid gap-4 md:grid-cols-3" data-testid="lb-podium">
            {top3.map((row, i) => (
              <div
                key={row.ambassador_id}
                data-testid={`lb-podium-${i + 1}`}
                className={`relative rounded-2xl border bg-zinc-950/80 p-6 text-center ${i === 0 ? "md:order-2 md:scale-105" : i === 1 ? "md:order-1" : "md:order-3"}`}
                style={{ borderColor: `${COLORS[i]}88`, boxShadow: `0 0 50px ${COLORS[i]}33` }}
              >
                {i === 0 && <Crown className="absolute -top-5 left-1/2 h-9 w-9 -translate-x-1/2" style={{ color: COLORS[0] }} />}
                <div className="font-mono text-sm" style={{ color: COLORS[i] }}>
                  RANK #{row.rank}
                </div>
                <div className="mt-2 font-display text-2xl font-black text-white">
                  {row.display_name}
                </div>
                {row.display_subtitle && (
                  <div className="mt-1 text-sm text-zinc-400">
                    {row.display_url ? (
                      <a href={row.display_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#A855F7]">
                        {row.display_subtitle} <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : row.display_subtitle}
                  </div>
                )}
                {row.display_meta && (
                  <div className="mt-0.5 text-xs text-zinc-500">{row.display_meta}</div>
                )}
                {showPoints && (
                  <>
                    <div className="mt-4 font-mono text-4xl font-black" style={{ color: COLORS[i] }}>
                      {row.points}
                    </div>
                    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">points</div>
                  </>
                )}
                {winners && i === 0 && winners.first?.reward && (
                  <div className="mt-3 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-300">
                    🏆 {winners.first.reward}
                  </div>
                )}
                {winners && i === 1 && winners.second?.reward && (
                  <div className="mt-3 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-300">
                    🥈 {winners.second.reward}
                  </div>
                )}
                {winners && i === 2 && winners.third?.reward && (
                  <div className="mt-3 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs text-zinc-300">
                    🥉 {winners.third.reward}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {data?.is_public_ready !== false && data?.top_10_pct_threshold > 0 && (
          <div className="mt-10 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-300/80">
            <Target className="h-3.5 w-3.5" /> Top 10% of cohort — first {data.top_10_pct_threshold} {data.top_10_pct_threshold === 1 ? "ambassador" : "ambassadors"}
          </div>
        )}

        {data?.is_public_ready !== false && rest.length > 0 && (
          <div className="mt-3 divide-y divide-zinc-900 rounded-xl border border-zinc-900 bg-zinc-950/60" data-testid="lb-list">
            {rest.map((row) => (
              <AmbassadorRow key={row.ambassador_id} row={row} showPoints={showPoints} />
            ))}
          </div>
        )}

        {data?.is_public_ready !== false && data?.total_active_ambassadors > 10 && (
          <div className="mt-6 rounded-xl border border-[#A855F7]/25 bg-gradient-to-br from-[#A855F7]/[0.06] to-zinc-950 p-5 text-center" data-testid="lb-want-to-be-here">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#A855F7]">Want your name here?</p>
            <p className="mt-2 text-sm text-zinc-300">
              Only the <span className="font-bold text-white">top 10</span> are public. {data.total_active_ambassadors - 10} other ambassadors are ranked privately — they see their position on their dashboard.
            </p>
            <Link
              to="/apply"
              data-testid="lb-apply-cta"
              className="mt-4 inline-flex items-center gap-2 rounded-md brand-gradient-bg px-5 py-2.5 text-sm font-bold text-white hover:-translate-y-0.5 transition-all"
            >
              Apply to next cohort
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        {winners && (
          <div className="mt-12 text-center">
            <h3 className="font-display text-2xl font-bold">Next cohort coming soon</h3>
            <p className="mt-2 text-sm text-zinc-400">Be the first to know. Apply now to join the waitlist.</p>
            <Link to="/apply" data-testid="lb-next-cohort-cta" className="mt-5 inline-flex rounded-md bg-[#A855F7] px-6 py-3 font-bold text-white">
              Apply for next cohort
            </Link>
          </div>
        )}
      </section>

      <SiteFooter />
    </div>
  );
}
